/**
 * Live now: every agent session that is working or waiting on a person, as one card each, with
 * what it is doing at this moment. Pure, so the order and the "now" line are tested.
 */
import type { NeedsYouSessionLevel, Session } from '@orgus/protocol';

/** 'asked': the turn ended on a question with nothing held (ADR 0056): listed with what needs you, quietly. */
export type LiveState = 'needs-you' | 'asked' | 'working';

export interface LiveCard {
  id: string;
  /** Which agent runs it (Claude Code, Codex, ...). */
  agentKind: Session['agentKind'];
  /** Input and output tokens so far (cache reads are cheap and loud, so left out). */
  tokens: number;
  /** True when the token count is a lower bound (a transcript only partly read). */
  partial: boolean;
  title: string;
  state: LiveState;
  /** What it is doing now, or what it asks: one line. */
  now: string;
  /** "Asks" when it waits on a person, "Now" when it works. */
  nowLabel: 'Asks' | 'Now';
  projectId: string | null;
  machineId: string;
  startedAt: number;
  lastActivity: number;
  toolCalls: number;
  subagentsRunning: number;
  /** The running workflow's progress over its phases' agents, when it has one. */
  progress: { done: number; total: number; name: string } | null;
  /** Its subagents and workflows: running first (newest first), then the latest finished. */
  children: LiveChild[];
}

export interface LiveChild {
  id: string;
  kind: 'subagent' | 'workflow';
  label: string;
  status: 'running' | 'done' | 'failed' | 'stopped';
  startedAt: number;
  endedAt: number | null;
  /** A workflow's agents done over all, across its phases. */
  progress?: { done: number; total: number };
}

/** How many finished children a card keeps under the running ones: the recent past, not the history. */
const FINISHED_CHILDREN = 3;

export function liveChildren(s: Pick<Session, 'subagents' | 'workflows'>): LiveChild[] {
  const kids: LiveChild[] = [
    ...s.workflows.map((w) => {
      const total = w.phases.reduce((n, p) => n + p.agents, 0);
      const done = w.phases.reduce((n, p) => n + p.done, 0);
      return { id: `w:${w.id}`, kind: 'workflow' as const, label: w.name, status: w.status, startedAt: w.startedAt, endedAt: w.endedAt, ...(total ? { progress: { done, total } } : {}) };
    }),
    ...s.subagents.map((a) => ({ id: `a:${a.id}`, kind: 'subagent' as const, label: a.description || a.type, status: a.status, startedAt: a.startedAt, endedAt: a.endedAt })),
  ];
  const running = kids.filter((k) => k.status === 'running').sort((a, b) => b.startedAt - a.startedAt);
  const finished = kids
    .filter((k) => k.status !== 'running')
    .sort((a, b) => (b.endedAt ?? b.startedAt) - (a.endedAt ?? a.startedAt))
    .slice(0, FINISHED_CHILDREN);
  return [...running, ...finished];
}

/** The first line of a message, without markdown emphasis or a leading list mark. */
export function firstLine(text: string): string {
  const line = text.split('\n').map((l) => l.trim()).find(Boolean) ?? '';
  return line.replace(/^[-*•]\s+/, '').replace(/[*_`]/g, '');
}

export function liveCard(s: Session, level?: NeedsYouSessionLevel | null): LiveCard | null {
  if (s.status !== 'working' && s.status !== 'waiting') return null;
  // An open (over a day) or dismissed question is not live work any more.
  if (s.status === 'waiting' && (level === 'open' || level === 'set-aside')) return null;
  const running = s.subagents.filter((a) => a.status === 'running');
  const newest = [...running].sort((a, b) => b.startedAt - a.startedAt)[0];
  const flow = s.workflows.find((w) => w.status === 'running');
  const total = flow ? flow.phases.reduce((n, p) => n + p.agents, 0) : 0;
  const done = flow ? flow.phases.reduce((n, p) => n + p.done, 0) : 0;
  const waiting = s.status === 'waiting';
  const said = firstLine(s.lastText);
  return {
    id: s.id,
    agentKind: s.agentKind,
    tokens: (s.tokens?.input ?? 0) + (s.tokens?.output ?? 0),
    partial: !!s.partial,
    title: s.title,
    state: waiting ? (level === 'asked' ? 'asked' : 'needs-you') : 'working',
    now: waiting ? said || 'Waiting for your answer' : newest ? newest.description || newest.type : said || 'Thinking',
    nowLabel: waiting ? 'Asks' : 'Now',
    projectId: s.projectId,
    machineId: s.machineId,
    startedAt: s.startedAt,
    lastActivity: s.lastActivity,
    toolCalls: s.toolCalls,
    subagentsRunning: running.length,
    progress: flow && total > 0 ? { done, total, name: flow.name } : null,
    children: liveChildren(s),
  };
}

/** What needs a person first (the longest wait on top), then work, the most recently active first. */
export function liveCards(sessions: readonly Session[], levels?: ReadonlyMap<string, NeedsYouSessionLevel>): LiveCard[] {
  const cards = sessions.map((s) => liveCard(s, levels?.get(s.id))).filter((c): c is LiveCard => c !== null);
  const rank = { 'needs-you': 0, asked: 1, working: 2 } as const;
  return cards.sort((a, b) => (a.state === b.state ? (a.state === 'working' ? b.lastActivity - a.lastActivity : a.lastActivity - b.lastActivity) : rank[a.state] - rank[b.state]));
}

export interface LiveGroup {
  projectId: string | null;
  cards: LiveCard[];
}

/**
 * Live cards by project, keeping each card's place (what needs you first): a project with a card
 * that needs you leads, then the busiest. No project groups last.
 */
export function liveGroups(cards: readonly LiveCard[]): LiveGroup[] {
  const by = new Map<string | null, LiveCard[]>();
  for (const c of cards) {
    const list = by.get(c.projectId) ?? [];
    list.push(c);
    by.set(c.projectId, list);
  }
  const asks = (g: LiveCard[]) => g.some((c) => c.state === 'needs-you');
  return [...by.entries()]
    .map(([projectId, list]) => ({ projectId, cards: list }))
    .sort((a, b) => Number(a.projectId === null) - Number(b.projectId === null) || Number(asks(b.cards)) - Number(asks(a.cards)) || b.cards.length - a.cards.length);
}

/** One square in the agent field: a session or one of its subagents. */
export interface FieldCell {
  id: string;
  /** The session that opens when the cell is clicked. */
  sessionId: string;
  kind: 'session' | 'subagent';
  state: 'needs-you' | 'working' | 'idle' | 'failed';
  label: string;
  /** Changes each time the agent does something (a turn, a tool call): the cell pulses once. */
  stamp: string;
}

export interface FieldGroup {
  /** The project or machine id the group is for (null: no project). */
  key: string | null;
  cells: FieldCell[];
}

export type FieldGrouping = 'project' | 'machine';

/**
 * Every live agent as one cell, grouped by project: each session that has not ended, and each
 * subagent that is running or failed under it. The busiest project first; within a group what
 * needs you, then what failed, then work, then idle.
 */
/** At most this many cells ripple per update: the Overview's live budget is 30 running animations (motion.md). */
export const FIELD_RIPPLES = 24;

/**
 * What moves in the field on this update, against the stamps last drawn (`prev`, null on the
 * first draw): a ripple for each cell whose agent did something since (its stamp changed, at
 * most `budget` of them), and the grow-in for cells that were not there. The first draw moves
 * nothing: 1,600 cells growing in and 1,400 ripples at once (box-shadow, painted every frame)
 * cost the Overview seconds of style and paint on opening (22 Oct 2026).
 */
export function fieldMotion(prev: ReadonlyMap<string, string> | null, cells: readonly FieldCell[], budget = FIELD_RIPPLES): { ripple: Map<string, string>; fresh: Set<string> } {
  const ripple = new Map<string, string>();
  const fresh = new Set<string>();
  if (!prev) return { ripple, fresh };
  for (const c of cells) {
    const was = prev.get(c.id);
    if (was === undefined) fresh.add(c.id);
    else if (was !== c.stamp && c.state !== 'idle' && ripple.size < budget) ripple.set(c.id, c.stamp);
  }
  return { ripple, fresh };
}

export function fieldGroups(sessions: readonly Session[], by: FieldGrouping = 'project'): FieldGroup[] {
  const order = { 'needs-you': 0, failed: 1, working: 2, idle: 3 } as const;
  const groups = new Map<string | null, FieldCell[]>();
  for (const s of sessions) {
    if (s.status === 'ended') continue;
    const key = by === 'machine' ? s.machineId : s.projectId;
    const cells = groups.get(key) ?? [];
    cells.push({ id: s.id, sessionId: s.id, kind: 'session', state: s.status === 'waiting' ? 'needs-you' : s.status === 'working' ? 'working' : 'idle', label: s.title, stamp: `${s.lastActivity}:${s.toolCalls}` });
    for (const a of s.subagents) {
      if (a.status !== 'running' && a.status !== 'failed') continue;
      cells.push({ id: `${s.id}:${a.id}`, sessionId: s.id, kind: 'subagent', state: a.status === 'failed' ? 'failed' : 'working', label: a.description || a.type, stamp: `${a.toolCalls}:${a.lastText.length}` });
    }
    groups.set(key, cells);
  }
  const busy = (c: FieldCell[]) => c.filter((x) => x.state !== 'idle').length;
  return [...groups.entries()]
    .map(([key, cells]) => ({ key, cells: cells.sort((a, b) => order[a.state] - order[b.state]) }))
    .sort((a, b) => busy(b.cells) - busy(a.cells) || b.cells.length - a.cells.length);
}
